import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronRight, User, Receipt, X } from 'lucide-react';
import api from '@/lib/axios';
import { CobroTab } from '@/pages/consultas/CobroTab';

type Cuenta = {
  citaId: string;
  pacienteId: string;
  clinicaId: string;
  totalItems: number;
  totalPagado: number;
  saldo: number;
  cita: { id: string; fechaInicio: string; motivo: string | null; estadoCobro: string } | null;
  paciente: { id: string; nombre: string; apellidoPaterno: string; apellidoMaterno?: string } | null;
};

function fmt(n: number) {
  return Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}
function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function CuentasPorCobrarPage() {
  const [selected, setSelected] = useState<Cuenta | null>(null);

  const { data: cuentas = [], isLoading } = useQuery<Cuenta[]>({
    queryKey: ['cuentas-por-cobrar'],
    queryFn: () => api.get('/api/finanzas/cuentas-por-cobrar').then(r => r.data),
  });

  const totalSaldo = cuentas.reduce((s, c) => s + c.saldo, 0);

  return (
    <div className="space-y-6">
      {/* KPI */}
      {cuentas.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle size={20} className="text-amber-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-amber-800">{fmt(totalSaldo)}</p>
            <p className="text-xs text-amber-700">{cuentas.length} cuenta{cuentas.length !== 1 ? 's' : ''} pendiente{cuentas.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="text-center py-12 text-text-secondary text-sm">Cargando...</div>
      ) : cuentas.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Receipt size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="font-semibold text-text-primary">Sin cuentas pendientes</p>
          <p className="text-sm text-text-secondary mt-1">Todos los cobros están al corriente</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
          {cuentas.map(c => {
            const nombre = c.paciente
              ? `${c.paciente.nombre} ${c.paciente.apellidoPaterno}${c.paciente.apellidoMaterno ? ' ' + c.paciente.apellidoMaterno : ''}`
              : 'Paciente';
            return (
              <button
                key={c.citaId}
                onClick={() => setSelected(c)}
                className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-9 h-9 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-gray-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-text-primary truncate">{nombre}</p>
                  <p className="text-xs text-text-secondary">
                    {c.cita ? fmtDate(c.cita.fechaInicio) : ''}{c.cita?.motivo ? ` · ${c.cita.motivo}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-amber-700">{fmt(c.saldo)}</p>
                  <p className="text-[10px] text-text-secondary">
                    de {fmt(c.totalItems)}
                  </p>
                </div>
                <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-5 py-4 rounded-t-2xl">
              <div>
                <p className="font-semibold text-text-primary">
                  {selected.paciente
                    ? `${selected.paciente.nombre} ${selected.paciente.apellidoPaterno}`
                    : 'Detalle de cuenta'}
                </p>
                {selected.cita && (
                  <p className="text-xs text-text-secondary">{fmtDate(selected.cita.fechaInicio)}{selected.cita.motivo ? ` · ${selected.cita.motivo}` : ''}</p>
                )}
              </div>
              <button onClick={() => setSelected(null)}>
                <X size={18} className="text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <div className="p-5">
              <CobroTab
                citaId={selected.citaId}
                pacienteId={selected.pacienteId}
                clinicaId={selected.clinicaId}
                canEdit={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
